import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Grid,
  Card,
  Badge,
  Button,
  Modal,
  TextInput,
  Select,
  NumberInput,
  Textarea,
  Group,
  Text,
  Table,
  Tabs,
  Alert,
  Switch,
  ActionIcon,
  Tooltip,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconParking,
  IconPlane,
  IconCash,
  IconSettings,
  IconCalendar,
  IconEdit,
} from "@tabler/icons-react";

import {
  createStand,
  updateStand,
  deleteStand,
  getOccupancyOverview,
  assignAircraftToStand,
  removeAircraftFromStand,
  getMyStandReservations,
  createStandReservation,
  getStandBilling,
  markBillingAsPaid,
} from "../misc/api";
import { DateInput } from "@mantine/dates";
import { getGroup } from "../misc/tokens";
import { AdminStands } from "./AdminStands";

const StandCard = ({
  stand,
  onAssignAircraft,
  onRemoveAircraft,
  onEditStand,
  userGroup,
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "occupied":
        return "red";
      case "vacant":
        return "green";
      case "departed":
        return "gray";
      default:
        return "gray";
    }
  };

  const getBillingStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "orange";
      case "paid":
        return "green";
      case "free":
        return "blue";
      case "billed":
        return "yellow";
      default:
        return "gray";
    }
  };

  const getBillingStatusText = (status, amount, hoursOccupied) => {
    if (hoursOccupied < 3) return "Free (under 3h)";

    switch (status) {
      case "pending":
        return `€${amount} pending`;
      case "paid":
        return `€${amount} paid`;
      case "free":
        return "Free";
      case "billed":
        return `€${amount} billed`;
      default:
        return status || "Free";
    }
  };

  return (
    <Card withBorder padding="md" radius="md">
      <Group position="apart" mb="xs">
        <Group>
          <IconParking size={20} />
          <Text weight={600}>{stand.stand_number}</Text>
          <Badge color={getStatusColor(stand.current_status)} size="sm">
            {stand.current_status}
          </Badge>
        </Group>
        <Group>
          <Badge variant="outline" size="xs">
            {stand.stand_type.replace("_", " ")}
          </Badge>
          {userGroup === "0" && (
            <ActionIcon onClick={() => onEditStand(stand)} size="sm">
              <IconEdit size={14} />
            </ActionIcon>
          )}
        </Group>
      </Group>

      {stand.current_status === "occupied" && (
        <>
          <Text size="sm" mb="xs">
            <IconPlane size={14} style={{ marginRight: 4 }} />
            {stand.aircraft_registration}
          </Text>
          {stand.pilot_name && (
            <Text size="xs" color="dimmed" mb="xs">
              Pilot: {stand.pilot_name}
            </Text>
          )}
          <Text size="xs" color="dimmed" mb="xs">
            Occupied: {stand.hours_occupied} hours
          </Text>
          <Text size="xs" color="dimmed" mb="xs">
            Since: {new Date(stand.occupied_from).toLocaleString()}
          </Text>
          <Badge
            color={getBillingStatusColor(stand.billing_status)}
            size="xs"
            mb="xs"
            style={{ display: "block", width: "fit-content" }}
          >
            {getBillingStatusText(
              stand.billing_status,
              stand.billing_amount,
              stand.hours_occupied
            )}
          </Badge>
        </>
      )}

      {(userGroup === "0" || userGroup === 100) && (
        <Group mt="md">
          {stand.current_status === "vacant" ? (
            <Button
              size="xs"
              variant="light"
              onClick={() => onAssignAircraft(stand)}
            >
              Assign Aircraft
            </Button>
          ) : (
            <Button
              size="xs"
              variant="light"
              color="red"
              onClick={() => onRemoveAircraft(stand)}
            >
              Mark Departed
            </Button>
          )}
        </Group>
      )}
    </Card>
  );
};

const AssignAircraftModal = ({ opened, onClose, stand, onSubmit }) => {
  const [form, setForm] = useState({
    aircraft_registration: "",
    aircraft_type: "",
    aircraft_weight_category: "light",
    pilot_name: "",
    pilot_phone: "",
    pilot_email: "",
    ppr_number: "",
    planned_departure: "",
    notes: "",
  });

  const handleSubmit = () => {
    onSubmit({ ...form, stand_id: stand?.stand_id });
    setForm({
      aircraft_registration: "",
      aircraft_type: "",
      aircraft_weight_category: "light",
      pilot_name: "",
      pilot_phone: "",
      pilot_email: "",
      ppr_number: "",
      planned_departure: "",
      notes: "",
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Assign Aircraft to Stand ${stand?.stand_number}`}
      size="md"
    >
      <TextInput
        label="Aircraft Registration"
        placeholder="LV-ABC"
        value={form.aircraft_registration}
        onChange={(e) =>
          setForm({
            ...form,
            aircraft_registration: e.target.value.toUpperCase(),
          })
        }
        required
        mb="sm"
      />

      <Select
        label="Aircraft Weight Category"
        value={form.aircraft_weight_category}
        onChange={(value) =>
          setForm({ ...form, aircraft_weight_category: value })
        }
        data={[
          { value: "light", label: "Light Aircraft (under 2000kg)" },
          { value: "heavy", label: "Heavy Aircraft (over 2000kg)" },
        ]}
        mb="sm"
      />

      <TextInput
        label="Pilot Name"
        value={form.pilot_name}
        onChange={(e) => setForm({ ...form, pilot_name: e.target.value })}
        mb="sm"
      />

      <TextInput
        label="Pilot Phone"
        value={form.pilot_phone}
        onChange={(e) => setForm({ ...form, pilot_phone: e.target.value })}
        mb="sm"
      />

      <TextInput
        label="Pilot Email"
        value={form.pilot_email}
        onChange={(e) => setForm({ ...form, pilot_email: e.target.value })}
        mb="sm"
      />

      <TextInput
        label="PPR Number (if applicable)"
        value={form.ppr_number}
        onChange={(e) => setForm({ ...form, ppr_number: e.target.value })}
        mb="sm"
      />

      <TextInput
        label="Planned Departure"
        type="datetime-local"
        value={form.planned_departure}
        onChange={(e) =>
          setForm({ ...form, planned_departure: e.target.value })
        }
        mb="sm"
      />

      <Textarea
        label="Notes"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        mb="md"
      />

      <Group position="right">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!form.aircraft_registration}>
          Assign Aircraft
        </Button>
      </Group>
    </Modal>
  );
};

const CreateStandModal = ({ opened, onClose, onSubmit, editingStand }) => {
  const [form, setForm] = useState({
    stand_number: "",
    stand_type: "light_aircraft",
    max_weight_kg: 2000,
    description: "",
    hourly_rate_light: 5.0,
    daily_rate_light: 50.0,
    monthly_rate_light: 300.0,
    hourly_rate_heavy: 8.0,
    daily_rate_heavy: 80.0,
    monthly_rate_heavy: 500.0,
    status: "active",
  });

  useEffect(() => {
    if (editingStand) {
      setForm({
        stand_number: editingStand.stand_number || "",
        stand_type: editingStand.stand_type || "light_aircraft",
        max_weight_kg: editingStand.max_weight_kg || 2000,
        description: editingStand.description || "",
        hourly_rate_light: editingStand.hourly_rate_light || 5.0,
        daily_rate_light: editingStand.daily_rate_light || 50.0,
        monthly_rate_light: editingStand.monthly_rate_light || 300.0,
        hourly_rate_heavy: editingStand.hourly_rate_heavy || 8.0,
        daily_rate_heavy: editingStand.daily_rate_heavy || 80.0,
        monthly_rate_heavy: editingStand.monthly_rate_heavy || 500.0,
        status: editingStand.status || "active",
      });
    } else {
      setForm({
        stand_number: "",
        stand_type: "light_aircraft",
        max_weight_kg: 2000,
        description: "",
        hourly_rate_light: 5.0,
        daily_rate_light: 50.0,
        monthly_rate_light: 300.0,
        hourly_rate_heavy: 8.0,
        daily_rate_heavy: 80.0,
        monthly_rate_heavy: 500.0,
        status: "active",
      });
    }
  }, [editingStand, opened]);

  const handleSubmit = () => {
    const submitData = editingStand
      ? { ...form, stand_id: editingStand.stand_id }
      : form;
    onSubmit(submitData);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        editingStand
          ? `Edit Stand ${editingStand?.stand_number}`
          : "Create New Stand"
      }
      size="lg"
    >
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Stand Number"
            placeholder="A1, B2, etc."
            value={form.stand_number}
            onChange={(e) =>
              setForm({ ...form, stand_number: e.target.value.toUpperCase() })
            }
            required
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            label="Stand Type"
            value={form.stand_type}
            onChange={(value) => setForm({ ...form, stand_type: value })}
            data={[
              { value: "light_aircraft", label: "Light Aircraft" },
              { value: "heavy_aircraft", label: "Heavy Aircraft" },
            ]}
            mb="sm"
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={6}>
          <NumberInput
            label="Max Weight (kg)"
            value={form.max_weight_kg}
            onChange={(value) => setForm({ ...form, max_weight_kg: value })}
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            label="Status"
            value={form.status}
            onChange={(value) => setForm({ ...form, status: value })}
            data={[
              { value: "active", label: "Active" },
              { value: "maintenance", label: "Maintenance" },
              { value: "disabled", label: "Disabled" },
            ]}
            mb="sm"
          />
        </Grid.Col>
      </Grid>

      <Textarea
        label="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        mb="md"
      />

      <Text weight={600} mb="sm">
        Light Aircraft Rates (€)
      </Text>
      <Grid>
        <Grid.Col span={4}>
          <NumberInput
            label="Hourly"
            value={form.hourly_rate_light}
            onChange={(value) => setForm({ ...form, hourly_rate_light: value })}
            precision={2}
            step={0.5}
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            label="Daily"
            value={form.daily_rate_light}
            onChange={(value) => setForm({ ...form, daily_rate_light: value })}
            precision={2}
            step={5}
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            label="Monthly"
            value={form.monthly_rate_light}
            onChange={(value) =>
              setForm({ ...form, monthly_rate_light: value })
            }
            precision={2}
            step={10}
            mb="sm"
          />
        </Grid.Col>
      </Grid>

      <Text weight={600} mb="sm">
        Heavy Aircraft Rates (€)
      </Text>
      <Grid>
        <Grid.Col span={4}>
          <NumberInput
            label="Hourly"
            value={form.hourly_rate_heavy}
            onChange={(value) => setForm({ ...form, hourly_rate_heavy: value })}
            precision={2}
            step={0.5}
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            label="Daily"
            value={form.daily_rate_heavy}
            onChange={(value) => setForm({ ...form, daily_rate_heavy: value })}
            precision={2}
            step={5}
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            label="Monthly"
            value={form.monthly_rate_heavy}
            onChange={(value) =>
              setForm({ ...form, monthly_rate_heavy: value })
            }
            precision={2}
            step={10}
            mb="md"
          />
        </Grid.Col>
      </Grid>

      <Group position="right">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!form.stand_number}>
          {editingStand ? "Update Stand" : "Create Stand"}
        </Button>
      </Group>
    </Modal>
  );
};

const CreateReservationModal = ({ opened, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    aircraft_registration: "",
    aircraft_weight_category: "light",
    start_date: "",
    months_paid: 1,
    auto_renew: false,
    payment_online: true,
  });

  const [selectedStand, setSelectedStand] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateCost = () => {
    if (!selectedStand) return 0;
    const monthlyRate = form.aircraft_weight_category === "heavy" ? 500 : 300;
    return monthlyRate * form.months_paid;
  };

  const handleSubmit = () => {
    onSubmit({
      ...form,
      stand_id: selectedStand?.stand_id,
      total_cost: calculateCost(),
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Reserve Long-term Parking"
      size="lg"
    >
      <LoadingOverlay visible={loading} />

      <TextInput
        label="Aircraft Registration"
        placeholder="LV-ABC"
        value={form.aircraft_registration}
        onChange={(e) =>
          setForm({
            ...form,
            aircraft_registration: e.target.value.toUpperCase(),
          })
        }
        required
        mb="sm"
      />

      <Select
        label="Aircraft Weight Category"
        value={form.aircraft_weight_category}
        onChange={(value) =>
          setForm({ ...form, aircraft_weight_category: value })
        }
        data={[
          { value: "light", label: "Light Aircraft (€300/month)" },
          { value: "heavy", label: "Heavy Aircraft (€500/month)" },
        ]}
        mb="sm"
      />

      <DateInput
        label="Start Date"
        value={form.start_date ? new Date(form.start_date) : null}
        onChange={(date) =>
          setForm({ ...form, start_date: date.split("T")[0] })
        }
        mb="sm"
      />

      <NumberInput
        label="Months to Pay"
        value={form.months_paid}
        onChange={(value) => setForm({ ...form, months_paid: value })}
        min={1}
        max={12}
        mb="sm"
      />

      <Switch
        label="Auto-renew subscription"
        checked={form.auto_renew}
        onChange={(event) =>
          setForm({ ...form, auto_renew: event.currentTarget.checked })
        }
        mb="sm"
      />

      {selectedStand && (
        <Alert icon={<IconCash size="1rem" />} mb="md">
          <Text size="sm">
            <strong>Stand:</strong> {selectedStand.stand_number} (
            {selectedStand.stand_type})<br />
            <strong>Total Cost:</strong> €{calculateCost()}
            <br />
            <strong>Period:</strong> {form.months_paid} month(s)
          </Text>
        </Alert>
      )}

      <Group position="right">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            !form.aircraft_registration || !form.start_date || !selectedStand
          }
        >
          Reserve & Pay €{calculateCost()}
        </Button>
      </Group>
    </Modal>
  );
};

const BillingTable = ({ billing, onMarkPaid, userGroup }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "green";
      case "pending":
        return "orange";
      case "overdue":
        return "red";
      default:
        return "gray";
    }
  };
  const rows = billing.map((bill) => (
    <tr key={bill.billing_id} style={{ textAlign: "center" }}>
      <td>{bill.aircraft_registration}</td>
      <td>{bill.billing_type.replace("_", " ")}</td>
      <td>€{bill.amount}</td>
      <td>
        <Badge color={getStatusColor(bill.status)}>{bill.status}</Badge>
      </td>
      <td>{new Date(bill.created_at).toLocaleDateString()}</td>
      <td>
        {(userGroup === "0" || userGroup === 100) &&
          bill.status === "pending" && (
            <Button size="xs" onClick={() => onMarkPaid(bill)}>
              Mark Paid
            </Button>
          )}
      </td>
    </tr>
  ));

  return (
    <Table>
      <thead>
        <tr>
          <th>Aircraft</th>
          <th>Type</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Date</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </Table>
  );
};

const StandManagementSystem = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [stands, setStands] = useState([]);
  const [myReservations, setMyReservations] = useState([]);
  const [billing, setBilling] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [assignModalOpened, setAssignModalOpened] = useState(false);
  const [reservationModalOpened, setReservationModalOpened] = useState(false);
  const [createStandModalOpened, setCreateStandModalOpened] = useState(false);
  const [selectedStand, setSelectedStand] = useState(null);
  const [editingStand, setEditingStand] = useState(null);

  const userGroup = getGroup(); // 0 = admin, 100 = duty personnel, 1 = regular user

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const standsData = await getOccupancyOverview();
      setStands(!standsData?.error ? standsData.data : []);

      if (userGroup === 1) {
        const reservationsData = await getMyStandReservations();
        setMyReservations(reservationsData || []);
      }

      if (userGroup === "0" || userGroup === 100) {
        const billingData = await getStandBilling({ limit: 50 });
        setBilling(billingData.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [userGroup]);

  useEffect(() => {
    loadData();

    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleAssignAircraft = (stand) => {
    setSelectedStand(stand);
    setAssignModalOpened(true);
  };

  const handleRemoveAircraft = async (stand) => {
    if (
      window.confirm(
        `Mark aircraft ${stand.aircraft_registration} as departed from ${stand.stand_number}?`
      )
    ) {
      try {
        setLoading(true);
        await removeAircraftFromStand(stand.occupancy_id, {
          actual_departure: new Date().toISOString(),
          notes: `Manually marked as departed by user`,
        });
        await loadData(); // Refresh data
      } catch (error) {
        console.error("Error removing aircraft:", error);
        alert("Error removing aircraft: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditStand = (stand) => {
    setEditingStand(stand);
    setCreateStandModalOpened(true);
  };

  const handleSubmitAssignment = async (assignmentData) => {
    try {
      setLoading(true);
      await assignAircraftToStand(assignmentData);
      setAssignModalOpened(false);
      await loadData(); // Refresh data
    } catch (error) {
      console.error("Error assigning aircraft:", error);
      alert("Error assigning aircraft: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReservation = async (reservationData) => {
    try {
      setLoading(true);
      const result = await createStandReservation(reservationData);
      setReservationModalOpened(false);

      if (result.payment_required && result.payment_info) {
        // Redirect to payment if needed
        alert(`Reservation created! Payment required: €${result.amount}`);
      }

      await loadData(); // Refresh data
    } catch (error) {
      console.error("Error creating reservation:", error);
      alert("Error creating reservation: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdateStand = async (standData) => {
    try {
      setLoading(true);

      if (editingStand) {
        await updateStand(standData.stand_id, standData);
      } else {
        await createStand(standData);
      }

      setCreateStandModalOpened(false);
      setEditingStand(null);
      await loadData(); // Refresh data
    } catch (error) {
      console.error("Error saving stand:", error);
      alert("Error saving stand: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkBillingPaid = async (bill) => {
    const paymentMethod =
      window.prompt("Payment method (cash/card/bank_transfer):") || "cash";
    const paymentReference =
      window.prompt("Payment reference (optional):") || "";

    if (
      window.confirm(
        `Mark billing for ${bill.aircraft_registration} (€${bill.amount}) as paid via ${paymentMethod}?`
      )
    ) {
      try {
        setLoading(true);
        await markBillingAsPaid(bill.billing_id, {
          payment_method: paymentMethod,
          payment_reference: paymentReference,
          notes: `Marked as paid by admin on ${new Date().toLocaleString()}`,
        });
        await loadData(); // Refresh data
      } catch (error) {
        console.error("Error marking billing as paid:", error);
        alert("Error updating billing: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const getTabsForUser = () => {
    const tabs = [
      {
        value: "overview",
        label: "Stand Overview",
        icon: <IconParking size={14} />,
      },
    ];

    if (userGroup === "1") {
      // Regular user
      tabs.push({
        value: "reservations",
        label: "My Reservations",
        icon: <IconCalendar size={14} />,
      });
    }

    if (userGroup === "0" || userGroup === 100) {
      // Admin or duty personnel
      tabs.push({
        value: "billing",
        label: "Billing",
        icon: <IconCash size={14} />,
      });
    }

    if (userGroup === "0") {
      // Admin only
      tabs.push({
        value: "management",
        label: "Stand Management",
        icon: <IconSettings size={14} />,
      });
    }

    return tabs;
  };

  return (
    <Container size="xl" py="md">
      <Group
        position="apart"
        mb="lg"
        style={{ display: "flex", alignItems: "center" }}
      >
        <Text size="xl" weight={700}>
          <IconParking size={28} style={{ marginRight: 8 }} />
          Apron Stand Management
        </Text>

        <Group>
          {userGroup !== "0" && (
            <Button
              leftIcon={<IconCalendar size={16} />}
              onClick={() => setReservationModalOpened(true)}
            >
              Reserve Long-term Parking
            </Button>
          )}

          {userGroup === "0" && (
            <Button
              leftIcon={<IconSettings size={16} />}
              onClick={() => {
                setEditingStand(null);
                setCreateStandModalOpened(true);
              }}
            >
              Add New Stand
            </Button>
          )}
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          {getTabsForUser().map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} icon={tab.icon}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <LoadingOverlay visible={loading} />
          <Grid>
            {stands.map((stand) => (
              <Grid.Col key={stand.stand_id} xs={12} sm={6} md={4} lg={3}>
                <StandCard
                  stand={stand}
                  onAssignAircraft={handleAssignAircraft}
                  onRemoveAircraft={handleRemoveAircraft}
                  onEditStand={handleEditStand}
                  userGroup={userGroup}
                />
              </Grid.Col>
            ))}
          </Grid>

          {stands.length === "0" && !loading && (
            <Text color="dimmed" ta="center" py="xl">
              No stands found.{" "}
              {userGroup === "0"
                ? "Create your first stand above."
                : "Contact admin to set up stands."}
            </Text>
          )}
        </Tabs.Panel>

        {userGroup === "1" && (
          <Tabs.Panel value="reservations" pt="md">
            <Card withBorder>
              <Text weight={600} mb="md">
                My Parking Reservations
              </Text>
              {myReservations.length === 0 ? (
                <Text color="dimmed" ta="center" py="xl">
                  No reservations found. Create your first long-term parking
                  reservation above.
                </Text>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <th>Stand</th>
                      <th>Aircraft</th>
                      <th>Period</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myReservations.map((reservation) => (
                      <tr key={reservation.reservation_id}>
                        <td>{reservation.stand_number}</td>
                        <td>{reservation.aircraft_registration}</td>
                        <td>
                          {new Date(
                            reservation.start_date
                          ).toLocaleDateString()}{" "}
                          -{new Date(reservation.end_date).toLocaleDateString()}
                        </td>
                        <td>
                          <Badge
                            color={
                              reservation.status === "active" ? "green" : "gray"
                            }
                          >
                            {reservation.status}
                          </Badge>
                        </td>
                        <td>€{reservation.total_amount}</td>
                        <td>
                          <Button size="xs" variant="light">
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </Tabs.Panel>
        )}

        {(userGroup === "0" || userGroup === 100) && (
          <Tabs.Panel value="billing" pt="md">
            <Card withBorder>
              <Text weight={600} mb="md">
                Billing Management
              </Text>
              <BillingTable
                billing={billing}
                onMarkPaid={handleMarkBillingPaid}
                userGroup={userGroup}
              />
            </Card>
          </Tabs.Panel>
        )}

        {userGroup === "0" && (
          <Tabs.Panel value="management" pt="md">
            <AdminStands />
          </Tabs.Panel>
        )}
      </Tabs>

      <AssignAircraftModal
        opened={assignModalOpened}
        onClose={() => setAssignModalOpened(false)}
        stand={selectedStand}
        onSubmit={handleSubmitAssignment}
      />

      <CreateReservationModal
        opened={reservationModalOpened}
        onClose={() => setReservationModalOpened(false)}
        onSubmit={handleCreateReservation}
      />

      <CreateStandModal
        opened={createStandModalOpened}
        onClose={() => {
          setCreateStandModalOpened(false);
          setEditingStand(null);
        }}
        onSubmit={handleCreateOrUpdateStand}
        editingStand={editingStand}
      />
    </Container>
  );
};

export default StandManagementSystem;
